import React from "react";
import { Zap, Star, ShieldCheck, Lock, Cpu, BadgeCheck } from "lucide-react";
import { repairEstimate, business } from "../mock/mock";
import Seo from "../seo/Seo";
import { PAGE_SEO } from "../seo/siteConfig";
import { breadcrumbSchema, webPageSchema } from "../seo/schema";
import Reveal from "../components/common/Reveal";
import ElfsightWidget from "../components/common/ElfsightWidget";

const benefitIcons = [Zap, Star, ShieldCheck, Lock, Cpu, BadgeCheck];

const RepairEstimate = () => {
  return (
    <>
      <Seo
        title={PAGE_SEO["/repair-estimate"].title}
        description={PAGE_SEO["/repair-estimate"].description}
        path="/repair-estimate"
        schema={[
          webPageSchema({ ...PAGE_SEO["/repair-estimate"], path: "/repair-estimate" }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Repair Estimate", path: "/repair-estimate" },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="relative min-h-[52vh] flex items-center overflow-hidden">
        <img
          src={repairEstimate.heroBg}
          alt="Free repair estimate"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
        <div className="tnf-container relative pt-32 pb-16">
          <Reveal className="max-w-2xl">
            <span className="tnf-eyebrow text-[var(--tnf-orange)]">
              Free Estimate
            </span>
            <h1 className="font-display text-white font-extrabold leading-[1.05] text-[40px] sm:text-[54px] mt-3">
              {repairEstimate.heroTitle}
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Booking */}
      <section className="py-20 lg:py-24">
        <div className="tnf-container">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Left: copy + benefits */}
            <Reveal className="lg:col-span-2">
              <span className="tnf-eyebrow">Book Online</span>
              <h2 className="font-display text-[28px] sm:text-[36px] font-extrabold text-[#0e0e10] mt-3 leading-[1.12]">
                {repairEstimate.subtitle}
              </h2>
              <p className="text-[#3b3b3b]/80 text-[16px] mt-4 leading-relaxed">
                {repairEstimate.text}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
                {repairEstimate.benefits.map((b, i) => {
                  const Icon = benefitIcons[i] || ShieldCheck;
                  return (
                    <div
                      key={b}
                      className="flex items-center gap-3 bg-white rounded-xl border border-black/5 px-4 py-3"
                    >
                      <span
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: "rgba(5,165,213,0.12)" }}
                      >
                        <Icon size={17} color="var(--tnf-orange)" />
                      </span>
                      <span className="text-[13.5px] font-semibold text-[#0e0e10] leading-tight">
                        {b}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 p-5 rounded-2xl bg-[#0e0e10] text-white">
                <div className="text-[13px] text-white/60">
                  Visit us · 2nd Level by the Food Court
                </div>
                <a
                  href={business.phoneRaw}
                  className="font-display text-[24px] font-extrabold hover:text-[var(--tnf-blue)] transition-colors"
                >
                  {business.phone}
                </a>
              </div>
            </Reveal>

            {/* Right: Elfsight Form Builder */}
            <Reveal delay={120} className="lg:col-span-3">
              <div className="bg-white rounded-[24px] border border-black/5 shadow-[0_18px_50px_rgba(0,0,0,0.06)] p-5 sm:p-7 min-h-[460px]">
                <ElfsightWidget appId="15165637-2fee-42fa-8fa2-cbb3140dd72c" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-20 lg:pb-28">
        <div className="tnf-container">
          <Reveal className="mb-8">
            <span className="tnf-eyebrow">Find Us</span>
            <h2 className="font-display text-[28px] sm:text-[38px] font-extrabold text-[#0e0e10] mt-3 leading-[1.1]">
              Find Us With Ease at Freehold Raceway Mall
            </h2>
            <p className="text-[#3b3b3b]/80 text-[15px] mt-2">
              We're on the <strong className="text-[#0e0e10] font-semibold">2nd level, right by the food court.</strong>
            </p>
          </Reveal>
          <Reveal
            delay={100}
            className="rounded-[24px] overflow-hidden border border-black/5 shadow-[0_18px_50px_rgba(0,0,0,0.08)] min-h-[420px]"
          >
            <iframe
              title="Talk N' Fix map"
              src={business.mapEmbed}
              className="w-full h-full min-h-[420px]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default RepairEstimate;
